import { env } from '@/shared/config/env';

interface TokenResponse {
  access_token: string;
  id_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
}

export function buildLoginUrl(): string {
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: env.cognitoDjClientId,
    redirect_uri: env.cognitoRedirectUri,
    scope: 'email openid profile',
  });
  return `${env.cognitoDomain}/oauth2/authorize?${params}`;
}

const GUEST_RETURN_KEY = 'vm_guest_return_to';

export function buildGuestLoginUrl(returnTo: string): string {
  sessionStorage.setItem(GUEST_RETURN_KEY, returnTo);
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: env.cognitoGuestClientId,
    redirect_uri: env.cognitoGuestRedirectUri,
    scope: 'email openid profile',
    // Also carry returnTo through Cognito via OAuth state: sessionStorage is
    // per-tab and gets lost when sign-up/verification lands in a new tab
    // (e.g. QR scanned from the phone camera), which forced a second scan.
    state: returnTo,
  });
  return `${env.cognitoDomain}/oauth2/authorize?${params}`;
}

export function consumeGuestReturnTo(): string | null {
  const val = sessionStorage.getItem(GUEST_RETURN_KEY);
  sessionStorage.removeItem(GUEST_RETURN_KEY);
  return val;
}

// Only accept app-internal paths so the state param can't be abused as an
// open redirect.
export function safeInternalPath(path: string | null): string | null {
  if (!path || !path.startsWith('/') || path.startsWith('//')) return null;
  return path;
}

export function buildLogoutUrl(): string {
  const params = new URLSearchParams({
    client_id: env.cognitoDjClientId,
    logout_uri: env.cognitoLogoutUri,
  });
  return `${env.cognitoDomain}/logout?${params}`;
}

async function postToToken(body: URLSearchParams): Promise<TokenResponse> {
  const res = await fetch(`${env.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Cognito token endpoint error ${res.status}: ${text}`);
  }
  return res.json() as Promise<TokenResponse>;
}

export function exchangeCode(code: string): Promise<TokenResponse> {
  return postToToken(new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: env.cognitoDjClientId,
    redirect_uri: env.cognitoRedirectUri,
    code,
  }));
}

export function exchangeGuestCode(code: string): Promise<TokenResponse> {
  return postToToken(new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: env.cognitoGuestClientId,
    redirect_uri: env.cognitoGuestRedirectUri,
    code,
  }));
}

export function refreshTokens(refreshToken: string): Promise<TokenResponse> {
  return postToToken(new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: env.cognitoDjClientId,
    refresh_token: refreshToken,
  }));
}

export function refreshGuestTokens(refreshToken: string): Promise<TokenResponse> {
  return postToToken(new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: env.cognitoGuestClientId,
    refresh_token: refreshToken,
  }));
}
